import { createSelector } from "reselect";
import { createAssign } from "robodux";
import { put, select } from "saga-query";

import { thunks } from "@app/state/apis/api";
import * as appConst from "@app/state/constants";
import { getMaxRole, getMinRole } from "@app/state/units/login";

import { selectHotAccess } from "../groups";
import { selectIsPasswordeless, selectMaxRole } from "../login";

import type { RootState } from "@app/state/rootState";
import type { TConstDrawerPages } from "@app/types/constant";
import type { TRoles, TDrawerItem, TMainDrawerContentrItem } from "@app/types";

// DRAWER ITEMS //
const availableDrawerItems: TDrawerItem[] = [
  {
    id: "profile",
    icon: "card",
    text: "Profil",
    enabled: true,
    visible: true,
    minCredential: "REGUEST",
  },
  {
    id: "showcase",
    icon: "palette",
    text: "Atelier",
    enabled: true,
    visible: true,
    minCredential: "JGUSER",
  },
  {
    id: "logout",
    icon: "close",
    text: "Iesire",
    enabled: true,
    visible: true,
    minCredential: "REGUEST",
  },
  {
    id: "swsadmin/users",
    icon: "user",
    text: "UserMan",
    enabled: true,
    visible: true,
    minCredential: "SWS",
  },
  {
    id: "swsadmin/groups",
    icon: "group",
    text: "OrgMan",
    enabled: true,
    visible: true,
    minCredential: "SWS",
  },
  {
    id: "swsadmin/showcase",
    icon: "palette",
    text: "Atelier",
    enabled: true,
    visible: true,
    minCredential: "SWS", // TODO: CHANGE TO JGUSER //
  },
  {
    id: "whatsnew",
    icon: "bulletlist",
    text: "Whatsnew",
    enabled: true,
    visible: true,
    minCredential: "SWS",
  },
  {
    id: "download",
    icon: "file",
    text: "Fisiere",
    enabled: true,
    visible: false,
    minCredential: "SWS",
  }
];
const insiderButtonData = ({
  CCode,
}: {
  CCode: string;
}): TMainDrawerContentrItem => {
  return {
    id: `insiders/${CCode}` as TMainDrawerContentrItem["id"], // Type assertion
    icon: 'data:image/svg+xml,%3Csvg xmlns="http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cg fill="none" stroke="%231b4461" stroke-linecap="round" stroke-width="2"%3E%3Cpath stroke-dasharray="32" stroke-dashoffset="32" d="M13 4L20 4C20.5523 4 21 4.44772 21 5V19C21 19.5523 20.5523 20 20 20H13"%3E%3Canimate fill="freeze" attributeName="stroke-dashoffset" dur="0.4s" values="32%3B0"%2F%3E%3C%2Fpath%3E%3Cpath stroke-dasharray="12" stroke-dashoffset="12" d="M3 12h11.5" opacity="0"%3E%3Cset attributeName="opacity" begin="0.5s" to="1"%2F%3E%3Canimate fill="freeze" attributeName="stroke-dashoffset" begin="0.5s" dur="0.2s" values="12%3B0"%2F%3E%3C%2Fpath%3E%3Cpath stroke-dasharray="6" stroke-dashoffset="6" d="M14.5 12l-3.5 -3.5M14.5 12l-3.5 3.5" opacity="0"%3E%3Cset attributeName="opacity" begin="0.7s" to="1"%2F%3E%3Canimate fill="freeze" attributeName="stroke-dashoffset" begin="0.7s" dur="0.2s" values="6%3B0"%2F%3E%3C%2Fpath%3E%3C%2Fg%3E%3C%2Fsvg%3E',
    text: CCode,
    enabled: true,
    visible: true,
    minCredential: "JGUSER",
  };
};
/* -------------------------------------------------------------------------- */

const REPO_UI_DRAWER = "ui/drawer";
type TRepoUiDrawer = {
  currentPage: TConstDrawerPages;
  insider: string; //!important//
  items: TDrawerItem[] | TMainDrawerContentrItem[] | [];
};

export const repoUiDrawer = createAssign({
  name: REPO_UI_DRAWER,
  initialState: {
    currentPage: appConst.DRAWER_PAGES.PROFILE as TConstDrawerPages,
    insider: "other",
    items: [] as TDrawerItem[],
  },
});
// drawer items
export const setUiDrawerItems = thunks.create(
  "setUiDrawerItems",
  function* (ctx, next) {
    const theMaxRole: TRoles = yield* select(selectMaxRole);
    // if is passwordless we limit the role to REGUEST
    const isPasswordless = yield* select(selectIsPasswordeless);

    const theRole = getMinRole([
      theMaxRole,
      isPasswordless ? "REGUEST" : theMaxRole,
    ]);

    const theItems = availableDrawerItems.filter((item) => {
      return getMaxRole([item.minCredential, theRole]) === theRole;
    });
    const crtstate: TRepoUiDrawer = yield* select(selectCurrentUiDrawer);
    yield* put(
      repoUiDrawer.actions.set({
        ...crtstate,
        items: theItems,
      })
    );
    yield* next();
  }
);

// Drawer pages
type TUIDrawer = Partial<TRepoUiDrawer>;

export const setUiDrawer = thunks.create<TUIDrawer>(
  "setUiDrawer",
  function* (ctx, next) {
    const crtstate = yield* select(selectCurrentUiDrawer);
    const newState = { ...crtstate, ...ctx.payload };
    // cleanup the insider page if we exit the insider zone //
    const isInsider = newState.currentPage.includes(
      appConst.DRAWER_PAGES.INSIDERS
    );
    if (!isInsider) {
      newState.insider = "";
    }
    yield* put(repoUiDrawer.actions.set(newState));
  }
);

export const clearInsiderSeed = thunks.create(
  "clearInsiderSeed",
  function* (ctx, next) {
    const crtstate = yield* select(selectCurrentUiDrawer);
    yield* put(repoUiDrawer.actions.set({ ...crtstate, insider: "" }));
  }
);

const selectUIDrawer = (state: RootState) => state[REPO_UI_DRAWER];
export const selectCurrentUiDrawer = createSelector(
  selectUIDrawer,
  (state) => state
);
export const selectCurrentUiDrawerPage = createSelector(
  selectUIDrawer,
  (state) => state.currentPage
);

export const selectUiDrawerItems = createSelector(
  selectUIDrawer,
  (state) => state.items
);

export const selectUiDrawerInsider = createSelector(
  selectUIDrawer,
  (state) => state.insider
);

/* -------------------------------------------------------------------------- */
// todo: https://github.com/VldMrgnn/jgap/issues/2 assign user to group in order to create a linik  between the role "@" and the perseed
// todo: https://github.com/VldMrgnn/jgap/issues/3 create a different menu for the insiders
// todo: https://github.com/VldMrgnn/jgap/issues/4 create infrastructure for the insiders to be able to work on their databases.
//
